import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useAuth } from "./AuthProvider";
// NOTE: We import `loading` from AuthProvider to avoid resolving label
// state while auth is still loading. Without this, pages see
// isLoading=false + label=null during auth init and think the user
// is a buyer, fetching unscoped data from all labels.
import { getUserLabel, Label, labelHostnamePrefix } from "@/lib/labels";

interface LabelContextType {
  label: Label | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  urlLabelPrefix: string | null;
  enforcedMismatch: boolean;
}

const LabelContext = createContext<LabelContextType | undefined>(undefined);

// Simple in-memory cache for label data
const labelCache = new Map<string, { data: Label | null; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function LabelProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [label, setLabel] = useState<Label | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [urlLabelPrefix, setUrlLabelPrefix] = useState<string | null>(null);
  const [currentHostname, setCurrentHostname] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const host = window.location.hostname.toLowerCase();
    setCurrentHostname(host);
    const match = host.match(/^([a-z0-9-]+)\.zenmusicgroup\.com$/);
    const prefix = match ? match[1] : null;
    // Don't treat 'app' as a label prefix - it's the main application domain
    setUrlLabelPrefix(prefix === 'app' ? null : prefix);
  }, []);

  const fetchLabel = useCallback(async () => {
    if (!user?.id) {
      // Only resolve as "no label" once auth has finished loading.
      // While auth is still loading, keep isLoading=true so pages wait.
      if (!authLoading) {
        setLabel(null);
        setIsLoading(false);
      }
      return;
    }

    // Check cache first
    const cached = labelCache.get(user.id);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      setLabel(cached.data);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await getUserLabel(user.id);
      if (result.success && result.data) {
        setLabel(result.data);
        // Cache the result
        labelCache.set(user.id, { data: result.data, timestamp: Date.now() });
      } else {
        setLabel(null);
        if (result.error) {
          setError(result.error);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch label");
      setLabel(null);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, authLoading]);

  const refetch = useCallback(async () => {
    if (user?.id) {
      // Clear cache for this user
      labelCache.delete(user.id);
    }
    await fetchLabel();
  }, [fetchLabel, user?.id]);

  useEffect(() => {
    fetchLabel();
  }, [fetchLabel]);

  const labelPrefix = label?.name ? labelHostnamePrefix(label.name) : null;
  const enforcedMismatch = Boolean(
    urlLabelPrefix && labelPrefix && urlLabelPrefix !== labelPrefix
  );

  useEffect(() => {
    if (!labelPrefix || !currentHostname) return;
    if (currentHostname !== "app.zenmusicgroup.com") return;
    if (typeof window === "undefined") return;

    const { pathname, search, hash } = window.location;

    // Never redirect auth or onboarding routes — auth callbacks carry tokens
    // in the URL hash that must be consumed on the original domain, and
    // onboarding handles its own redirect after completion.
    if (pathname.startsWith('/auth/') || pathname.startsWith('/onboarding')) {
      return;
    }

    // Don't redirect on public share routes - these should stay on app domain
    if (pathname.startsWith('/storefront/share/') ||
        pathname.startsWith('/playlist/share/') ||
        pathname.startsWith('/shared/') ||
        pathname.startsWith('/track/shared/')) {
      return;
    }

    window.location.replace(
      `https://${labelPrefix}.zenmusicgroup.com${pathname}${search}${hash}`
    );
  }, [labelPrefix, currentHostname]);

  const value = {
    label,
    isLoading,
    error,
    refetch,
    urlLabelPrefix,
    enforcedMismatch,
  };

  let content = children;
  if (enforcedMismatch && labelPrefix && currentHostname) {
    const redirectHost = `${labelPrefix}.zenmusicgroup.com`;
    const handleRedirect = () => {
      const { pathname, search, hash } = window.location;
      window.location.replace(`https://${redirectHost}${pathname}${search}${hash}`);
    };

    content = (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background text-center px-6">
        <div className="max-w-md space-y-4">
          <h1 className="text-2xl font-semibold text-foreground">
            You&apos;re signed into {label?.name}
          </h1>
          <p className="text-muted-foreground">
            But you&apos;re currently visiting {currentHostname}. Please use the
            link below to access your label&apos;s workspace.
          </p>
          <button
            onClick={handleRedirect}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-primary-foreground font-medium transition hover:opacity-90"
          >
            Go to {redirectHost}
          </button>
        </div>
      </div>
    );
  } else if (user && !isLoading && !label && urlLabelPrefix && currentHostname) {
    content = (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background text-center px-6">
        <div className="max-w-md space-y-4">
          <h1 className="text-2xl font-semibold text-foreground">
            Label access required
          </h1>
          <p className="text-muted-foreground">
            You&apos;re visiting {currentHostname}, but your account is not yet
            associated with this label. Please contact an administrator or head
            back to the main app.
          </p>
          {error && (
            <p className="text-sm text-destructive/80 font-mono">
              Debug: {error}
            </p>
          )}
          <button
            onClick={() =>
              window.location.replace(
                `https://app.zenmusicgroup.com${window.location.pathname}${window.location.search}${window.location.hash}`
              )
            }
            className="inline-flex items-center justify-center rounded-md border border-border px-4 py-2 text-foreground font-medium transition hover:bg-muted"
          >
            Go to app.zenmusicgroup.com
          </button>
        </div>
      </div>
    );
  }

  return (
    <LabelContext.Provider value={value}>{content}</LabelContext.Provider>
  );
}

export function useLabel(): LabelContextType {
  const context = useContext(LabelContext);
  if (context === undefined) {
    throw new Error("useLabel must be used within a LabelProvider");
  }
  return context;
}

/**
 * Non-throwing label accessor for purely presentational components that can
 * degrade gracefully when rendered outside a LabelProvider (portals, stale HMR
 * trees, tests/Storybook). Returns `undefined` instead of crashing the app.
 */
export function useOptionalLabel(): LabelContextType | undefined {
  return useContext(LabelContext);
}

// NOTE: `getLabelCoverImageUrl` was removed with the storefront tenant-isolation fix.
// Its only caller rendered the *viewer's* label cover as another tenant's storefront
// branding. A storefront's imagery must come from the storefront payload; if a future
// caller needs a label's own cover, resolve it from that label's record explicitly and
// never from `useLabel()` inside a surface that displays someone else's entity.
